import React, { useState, useRef, useEffect } from "react";
import PhoneInput from "react-phone-number-input";
import Swal from "sweetalert2";
import { isValidPhoneNumber } from "react-phone-number-input";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import "react-phone-number-input/style.css";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import googleCalendarPlugin from '@fullcalendar/google-calendar';
import soc2 from "../../assets/soc_2.png";
import SEO from "@/components/SEO";
import { seoConfig } from "@/config/seo.config";
import GoogleCalendarWidget from "./GoogleCalendarWidget";
import GoogleCalendarPopup from "./GoogleCalendarPopUp";

interface FormData {
  firstname: string;
  lastname: string;
  email: string;
  companyName: string;
  phone: string;
}

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  formData: FormData;
  handleCloseCalendlyPopup: () => void;
}

// In the Popup component:
const Popup: React.FC<PopupProps> = ({
  isOpen,
  formData,
  handleCloseCalendlyPopup,
}) => {
  const [isScheduling, setIsScheduling] = useState(false);

  // Add a useEffect to set up a global event listener for Calendly
  useEffect(() => {
    // Define the event handler function
    const handleMessage = (e: MessageEvent) => {
      console.log("Received message:", e.data);
      // if (e.data.event && e.data.event.indexOf('calendly') === 0) {
        console.log("Calendly event received:", e.data.event);
        
        // Check if this is a scheduling completion event
        if (e.data.event === 'scheduled') {
          console.log("Scheduling event detected, closing popup...");
          setIsScheduling(true);
          
          // Close popup and redirect
          setTimeout(() => {
            handleCloseCalendlyPopup();
          }, 500);
        // }
      }
    };

    // Add the event listener
    window.addEventListener('message', handleMessage);

    // Clean up
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleCloseCalendlyPopup]);

  // Keep the existing Calendly event listener for other events
  useCalendlyEventListener({
    onProfilePageViewed: () => console.log("Profile page viewed"),
    onDateAndTimeSelected: () => console.log("Date and time selected"),
    onEventTypeViewed: () => console.log("Event type viewed"),
    onEventScheduled: (e) => console.log("Event scheduled:", e.data.payload),
    onPageHeightResize: (e) =>
      console.log("Page height resized:", e.data.payload.height),
  });

  if (!isOpen) return null;

  return (
    <>
    <SEO {...seoConfig.demo} />
    <div className="mx-4 fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black opacity-50"
        onClick={handleCloseCalendlyPopup}
      ></div>
      <div className="relative bg-white rounded-lg w-auto">
        {isScheduling && (
          <div className="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-[#06526D] border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-[#06526D] font-medium">Scheduling your demo...</p>
            </div>
          </div>
        )}
        <div className="md:px-6 pt-5 px-3">
          <div className="flex justify-between gap-4 sm:gap-16 items-center text-gray-700">
            <div>
              <div className="text-[18px] font-semibold">
                We have received your demo request successfully.
              </div>
              <div className="text-[14px] font-medium">
                Book your own time slot.
              </div>
            </div>
            <button
              onClick={handleCloseCalendlyPopup}
              className="text-gray-500 hover:text-gray-700 hamburger "
            >
              <div className="hover:bg-gray-100">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="gray"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
        <div className="md:p-6 p-4">
          <InlineWidget
            url="https://savvycal.com/harshdeep/1381cd4b"
            prefill={{
              name: `${formData.firstname} ${formData.lastname}`,
              email: formData.email,
            }}
            styles={{
              width: "100%",
              maxWidth: "520px",
              height: "600px",
              borderRadius: "50px",
              zIndex: 60,
            }}
          />
        </div>
      </div>
    </div>
    </>
  );
};
interface ScheduleDemoProps {
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isOpen: boolean) => void;
}
const ScheduleDemo: React.FC<ScheduleDemoProps> = ({
  isCalendarOpen,
  setIsCalendarOpen,
}) => {
  const [formData, setFormData] = useState<FormData>({
    firstname: "",
    lastname: "",
    email: "",
    companyName: "",
    phone: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (sectionRef.current) {
      const { top } = sectionRef.current.getBoundingClientRect();
      const isInView = top < window.innerHeight * 0.5 && top >= 0;
      setIsScrolled(isInView);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // In the Popup component, add a state to track if an event was scheduled
  const [eventScheduled, setEventScheduled] = useState(false);
  
  // Update the Calendly event listener
  // useCalendlyEventListener({
  //   onProfilePageViewed: () => console.log("Profile page viewed"),
  //   onDateAndTimeSelected: () => console.log("Date and time selected"),
  //   onEventTypeViewed: () => console.log("Event type viewed"),
  //   onEventScheduled: (e) => {
  //     console.log("Event scheduled:", e.data.payload);
  //     setEventScheduled(true);
  //   },
  // });
  
  // Then modify handleCloseCalendlyPopup
  const handleCloseCalendlyPopup = () => {
    console.log("Closing popup and redirecting...");
    setIsPopupOpen(false);
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      companyName: "",
      phone: "",
    });
    
    // Add a small delay before redirecting to ensure the popup is closed
    setTimeout(() => {
      window.location.href = "/success";
    }, 100);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handlePhoneChange = (value: string | undefined): void => {
    setFormData({ ...formData, phone: value ?? "" });
  };

  const scheduleDemo = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    setIsLoading(true);
    const { email, phone } = formData;

    if (!validateEmail(email)) {
      Swal.fire("Error!", "Email is invalid!", "error");
      setIsLoading(false);
      return;
    }
    if (phone && !isValidPhoneNumber(phone)) {
      Swal.fire("Error!", "Phone number is invalid!", "error");
      setIsLoading(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://prodapi2.legittai.com/api/notification/sendMail",
        {
          method: "POST",
          body: JSON.stringify({
            ...formData,
            senderEmail:
              "gaurav.verma@legittai.com,harshdeep.rapal@legittai.com",
          }),
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
          },
        }
      );

      if (response.ok) {
        setIsPopupOpen(true);
      } else {
        Swal.fire(
          "Error!",
          "Something went wrong while sending email. Try again or email at info@legittmateai.com!",
          "error"
        );
      }
    } catch (err) {
      console.error("Error:", err);
      Swal.fire(
        "Error!",
        "Something went wrong while sending email. Try again or email at info@legittmateai.com!",
        "error"
      );
      setFormData({
        firstname: "",
        lastname: "",
        email: "",
        companyName: "",
        phone: "",
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };
 return (
    <div className=" flex justify-center p-[25px]  text-gray-700 rounded-xl h-auto   ">      
      <GoogleCalendarPopup isCalendarOpen={isCalendarOpen} setIsCalendarOpen={setIsCalendarOpen}/>
    </div>
  );
  // return (
  //   <div className="p-[25px] bg-white text-gray-700 rounded-md h-auto w-full">
  //     <form className={`flex flex-col gap-2 ${isScrolled ? "scrolled" : ""}`}>
  //       <div className="grid grid-cols-1 513sm:grid-cols-2 gap-2">
  //         <div className="relative">
  //           <input
  //             required
  //             id="firstname"
  //             type="text"
  //             value={formData.firstname}
  //             onChange={handleChange}
  //             className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none hover:ring-1 focus:ring-[#06526D] text-[#666666]"
  //             placeholder="First Name"
  //           />
  //         </div>
  //         <div className="relative">
  //           <input
  //             required
  //             id="lastname"
  //             type="text"
  //             value={formData.lastname}
  //             onChange={handleChange}
  //             className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none hover:ring-1 focus:ring-[#06526D] text-[#666666]"
  //             placeholder="Last Name"
  //           />
  //         </div>
  //       </div>

  //       <input
  //         id="companyName"
  //         type="text"
  //         value={formData.companyName}
  //         onChange={handleChange}
  //         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none hover:ring-1 focus:ring-[#06526D] text-[#666666]"
  //         placeholder="Company"
  //       />

  //       <input
  //         required
  //         id="email"
  //         type="email"
  //         value={formData.email}
  //         onChange={handleChange}
  //         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none hover:ring-1 focus:ring-[#06526D] text-[#666666]"
  //         placeholder="Email Address"
  //       />

  //       <PhoneInput
  //         className="w-full bg-white py-[15px] px-[11px] demo-contact-number-text-field"
  //         placeholder="Enter phone number"
  //         value={formData.phone}
  //         onChange={handlePhoneChange}
  //         defaultCountry="US"
  //       />

  //       <div className="flex flex-col justify-center items-center mt-[10px]">
  //         <button
  //           onClick={scheduleDemo}
  //           disabled={isSubmitting || isLoading}
  //           className={`px-6 py-2 bg-[#06526D] text-white rounded-md hover:bg-[#069fc0] transition-colors
  //             ${
  //               isSubmitting || isLoading ? "opacity-50 cursor-not-allowed" : ""
  //             }`}
  //         >
  //           {isLoading ? (
  //             <div className="flex items-center justify-center">
  //               <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
  //             </div>
  //           ) : (
  //             "Schedule Time"
  //           )}
  //         </button>

  //         <div className="mt-2 text-[0.8rem] lg:text-[0.9rem] font-normal">
  //           By scheduling demo, you accept our{" "}
  //           <a
  //             className="font-semibold text-primary-main hover:underline"
  //             href="/privacy-policy"
  //             target="_blank"
  //             rel="noopener noreferrer"
  //           >
  //             privacy policy
  //           </a>{" "}
  //           &{" "}
  //           <a
  //             className="font-semibold text-primary-main hover:underline"
  //             href="/terms-and-conditions"
  //             target="_blank"
  //             rel="noopener noreferrer"
  //           >
  //             terms and conditions
  //           </a>
  //           .
  //         </div>
  //       </div>
  //     </form>

  //     <Popup
  //       isOpen={isPopupOpen}
  //       onClose={handleCloseCalendlyPopup}
  //       formData={formData}
  //       handleCloseCalendlyPopup={handleCloseCalendlyPopup}
  //     />
  //   </div>
  // );
};
interface LegittLiteDemoBannerProps {
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isOpen: boolean) => void;
}

const LegittLiteDemoBanner: React.FC<LegittLiteDemoBannerProps> = ({
  isCalendarOpen,
  setIsCalendarOpen,
}: LegittLiteDemoBannerProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (sectionRef.current) {
      const { top } = sectionRef.current.getBoundingClientRect();
      const isInView = top < window.innerHeight * 0.5 && top >= 0;
      setIsScrolled(isInView);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div>
      <div
        className={`flex flex-wrap mb-[100px] lg:flex-nowrap ${
          isScrolled ? "md:px-[5%] px-[4%]" : "md:px-[4%] px-[4%]"
        } transition-all duration-500 ease-in-out justify-center items-center rounded-lg gap-6`}
        ref={sectionRef}
      >
        <div className=" w-full lg:w-[100%]">
          <div
            className={`relative isolate flex flex-col justify-center items-center overflow-hidden ${
              isScrolled
                ? "bg-gradient-to-b from-[#06526D] to-[#069fc0] text-white border-2 border-[#06526D]"
                : "bg-gray-100 text-gray-700"
            }  py-[50px] rounded-[50px] transition-all duration-300 ease-in-out`}
          >
            <div className=" flex justify-center font-bold text-[36px] sm:text-[44px] lg:text-[40px] mb-[10px] rounded-lg mt-0 ">
              Talk to our Experts
            </div>
            <p className="flex justify-center text-[20px] sm:text-[20px] lg:text-[24px] mt-0 mb-[40px] ">
              Schedule a personalized consultation
            </p>
            <div className="mx-auto px-[15px] md:px-[30px] justify-center items-center lg:px-[50px] xl:px-[50px]">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <dl className="">
                  <div className="w-full">
                    {/* <div className=" flex justify-center font-bold text-[36px] sm:text-[44px] lg:text-[40px] mb-[10px] rounded-lg mt-0 ml-55">
                      Talk to our Experts
                    </div>
                    <p className="flex justify-center text-[20px] sm:text-[20px] lg:text-[24px] mt-0 mb-[40px] ml-50">
                      Schedule a personalized consultation
                    </p> */}

                    <div className="mt-6 space-y-4">
                      {/* Step 1 */}
                      <div className="flex items-center space-x-4">
                        <span
                          className={`text-white ${
                            isScrolled ? "bg-black" : "bg-[#06526D]"
                          } min-w-8 max-w-8 h-8 flex items-center justify-center rounded-full font-bold`}
                        >
                          1
                        </span>
                        <p className="text-[16px] sm:text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px]">
                          Schedule
                        </p>
                      </div>

                      {/* Step 2 */}
                      <div className="flex items-center space-x-4">
                        <span
                          className={`text-white ${
                            isScrolled ? "bg-black" : "bg-[#06526D]"
                          } min-w-8 max-w-8 h-8 flex items-center justify-center rounded-full font-bold`}
                        >
                          2
                        </span>
                        <p className="text-[16px] sm:text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px] ">
                          Discuss your requirement + See product Demo
                        </p>
                      </div>

                      {/* Step 3 */}
                      <div className="flex items-center justify-top space-x-4">
                        <span
                          className={`text-white ${
                            isScrolled ? "bg-black" : "bg-[#06526D]"
                          } min-w-8 max-w-8 h-8 flex items-center justify-center rounded-full font-bold`}
                        >
                          3
                        </span>
                        <p className="text-[16px] sm:text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px]">
                          Discover how we can simplify your sales process to
                          <br />
                          save time and close deals faster.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap bg-white gap-1 xs:gap-2 items-center justify-around mt-[50px] sm:mt-[50px] md:mt-[30px] p-2 rounded-[12px] w-[100%] lg:ml-[250px] ">
                    <img
                      src="https://d2qb2qddg7l6c6.cloudfront.net/assets/images/ISO27701.png"
                      alt="iso27701"
                      className="w-auto h-[3rem] sm:h-[3.5rem] lg:h-[4rem] xl:h-[5.5rem]"
                      loading="lazy"
                    />
                    <img
                      src="https://d2qb2qddg7l6c6.cloudfront.net/assets/images/ISO27001.png"
                      alt="iso27001"
                      className="w-auto h-[3rem] sm:h-[3.5rem]  lg:h-[4rem] xl:h-[5rem]"
                      loading="lazy"
                    />

                    <img
                      src="https://d2qb2qddg7l6c6.cloudfront.net/assets/images/GdprCertification.png"
                      alt="gdpr"
                      className="w-auto h-[3rem] sm:h-[3.5rem] lg:h-[4rem] xl:h-[5rem]"
                      loading="lazy"
                    />
                    <img
                      src={soc2}
                      alt="soc"
                      className="w-auto h-[3rem] sm:h-[3.5rem] lg:h-[4rem] xl:h-[5rem]"
                      loading="lazy"
                    />
                  </div>
                </dl>
                <div className={`bg-white ${
                            isScrolled ? " border-[#37DAFF]" : "border-[#000]"
                          } w-[full] justify-center items-center border-2 mt-6 md:mt-0 lg:mt-0 rounded-[20px] p-4 shadow-lg mb-40 hover:shadow-xl hover:border-3 transition-all duration-50 ease-linear`}>
                  <ScheduleDemo
                    // onOpenPopup={() => {
                    //   console.log("Opening popup");
                    // }}
                  isCalendarOpen={isCalendarOpen} setIsCalendarOpen={setIsCalendarOpen}/>
                  <div className="text-black text-[14px]">By scheduling a demo, you accept our {" "}
                    <a className={"font-extrabold text-blue-600 underline decoration-solid"} href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy {" "}</a>& {" "}
                    <a className="font-extrabold text-blue-600 underline decoration-solid"  href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">terms and conditions</a>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegittLiteDemoBanner;
